import { createRef, useEffect } from "react";
import {
  createBrowserRouter,
  redirect,
  useMatches,
  useLocation,
} from "react-router-dom";
import { ErrorRouteProvider } from "../errors/ErrorRouteContext";
import { LayoutProvider } from "../providers/LayoutProvider";

// app layout
import MainLayout from "../layout/MainLayout";

// route pages
import HomePage from "../pages/Home";
import AboutPage from "../pages/About";
import TestPage from "../tests/TestPage";
import ErrorPage from "../errors/ErrorPage";
import RouteErrorPage from "../components/errors/RouteErrorPage";

const ctx = require.context("../features", true, /\/routes\/index\.js$/);

/**
 * Base list of route configuration objects used by the application's router.
 *
 * Each entry describes a route pattern, the element to render, and optional
 * per-route metadata consumed by layouts, navigation UI, and scroll handlers.
 *
 * @typedef {Object} Route
 * @property {string} path
 *   The URL path pattern for the route (e.g. "/", "/about"). Use "*" for a
 *   fallback/not-found route.
 *
 * @property {import('react').ReactNode} element
 *   The React element rendered when the route matches.
 *
 * @property {boolean} [index]
 *   When true marks this route as an index (default) route for its parent.
 *
 * @property {Handle} [handle]
 *   Arbitrary per-route metadata used by the application's layout, navigation,
 *   and scroll management logic.
 *
 * @typedef {Object} Handle
 * @property {string} [name]
 *   Human-readable name for the route (used for titles, breadcrumbs, nav items).
 *
 * @property {import('react').RefObject<HTMLElement>} [nodeRef]
 *   A React ref to the route's root DOM node (commonly created via createRef()).
 *
 * @property {import('react').ComponentType|function} [layout]
 *   Optional layout component used to wrap this route's element.
 *
 * @property {"top"|"remember"} [scrollState]
 *   Per-route scroll behavior:
 *     - "top"    = always scroll to top when navigating to this route
 *     - "remember" (or omitted) = allow the browser/router to remember scroll pos
 *
 * @constant {Route[]} baseRoutes
 *   The exported array of route descriptors.
 */
const baseRoutes = [
  {
    path: "/",
    element: <HomePage />,
    index: true,
    handle: {
      name: "Home",
      nodeRef: createRef(),
      layout: MainLayout,
      scrollState: "top",
    },
  },
  {
    path: "/about",
    element: <AboutPage />,
    handle: {
      name: "About",
      nodeRef: createRef(),
    },
  },
  {
    path: "/test",
    element: <TestPage />,
    handle: {
      name: "Test",
      nodeRef: createRef(),
    },
  },
  //
  {
    path: "/error",
    element: <ErrorPage />,
    handle: {
      name: "Error",
      nodeRef: createRef(),
    },
  },
  {
    path: "*",
    element: <div>Not Found</div>,
    handle: {
      name: "Not Found",
      nodeRef: createRef(),
    },
  },
];

// collect feature routes

// [ ] Release checklist: don't compile disabled features
const disabledFeatures = ["itinerary", "shrtr"];

const featureRoutes = ctx.keys().flatMap((k) => {
  disabledFeatures.forEach((feature) => {
    if (k.includes(feature)) {
      console.debug("skipping " + feature + " routes.");
      return []; //return empty routes for skipped features
    }
  });

  const mod = ctx(k);
  return mod.default ?? mod.routes ?? [];
});

function LayoutSwitch({ defaultLayout: DefaultLayout = MainLayout }) {
  const matches = useMatches();

  const Layout = (() => {
    for (let i = matches.length - 1; i >= 0; i--) {
      const L = matches[i]?.handle?.layout;
      if (L) return L;
    }
    return DefaultLayout;
  })();

  return <Layout />; // <-- layouts themselves contain <Outlet/>
}

/**
 * ScrollManager
 * - Looks at the current route match stack and applies per-route scroll policy
 *   using handle.scrollState.
 *
 * Supported values:
 *   - "top"      -> always scroll to top on navigation
 *   - "remember" -> do nothing, let browser/router manage scroll
 *   - undefined  -> treated as defaultBehavior
 */
function ScrollManager({ defaultBehavior = "remember" }) {
  const location = useLocation();
  const matches = useMatches();

  useEffect(() => {
    // Find the deepest match that specifies a scrollState
    let scrollState = defaultBehavior;

    for (let i = matches.length - 1; i >= 0; i--) {
      const routeScrollState = matches[i]?.handle?.scrollState;
      if (routeScrollState) {
        scrollState = routeScrollState;
        break;
      }
    }

    if (scrollState === "top") {
      // You can change behavior to "smooth" if you prefer
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
    // For "remember" we do nothing: default browser / ScrollRestoration behavior

    // We mimic your original ScrollToTop behavior and key off pathname.
    // If you want *every* navigation (even search/hash changes) to count,
    // you could use `location.key` instead of `location.pathname`.
  }, [location.pathname, matches, defaultBehavior]);

  return null;
}

/**
 * RootLayout’s job is to mount any providers that should live
 * *inside* the router tree (so descendants can use them).
 *
 * Providers that need router context live under RootLayout.
 */
function RootLayout() {
  return (
    <ErrorRouteProvider>
      {/* 👆 must live ABOVE all children including /error */}
      <LayoutProvider>
        {/* 👇 Per-route scroll handling */}
        <ScrollManager defaultBehavior="remember" />
        <LayoutSwitch /> {/* your app shell / outlet lives here */}
      </LayoutProvider>
    </ErrorRouteProvider>
  );
}

const router = createBrowserRouter([
  {
    path: "/",
    element: <RootLayout />,
    errorElement: <RouteErrorPage />, // catches loader/action/render errors in this branch
    children: [...baseRoutes, ...featureRoutes],
  },
]);

export default router;
